import { H3 } from '@h3/types';
import * as Api from '../../api';
import { BaseModule } from './base';

enum Type {
    frequentlyApps = 'frequentlyApps',
}

enum FrequentlyAppType {
    PC,
    PCDefault,
    Mobile,
    MobileDefault,
}

type TFrequentlyAppMap = Partial<Record<FrequentlyAppType, H3.Preference.Request.IFrequentlyAppItem>>;

export default class PreferenceState extends BaseModule {
    private _frequentlyAppMap?: TFrequentlyAppMap;

    private _loadingPromiseMap: Partial<Record<Type, Promise<boolean>>> = {};

    /**
     * 获取给定场景展示的常用应用数据
     * @param device 平台类型
     * @param isDefault 是否默认数据
     * @returns 常用应用
     */
    getFrequentlyApps(device: H3.Util.Device, isDefault: boolean): H3.FunctionNode.IFunctionNode[] {
        if (!this._frequentlyAppMap) {
            return [];
        }

        const res = this._frequentlyAppMap?.[this._getFrequentlyAppType(device, isDefault)]?.appCodes
            .map((appCode) => this._engineContext.appsMap[appCode])
            .filter(Boolean);

        if (res) {
            return res;
        }

        if (isDefault) {
            return [];
        }

        return this.getFrequentlyApps(device, true);
    }

    async loadFrequentlyApps(force = false) {
        if (this._frequentlyAppMap && !force) {
            return true;
        }
        if (!this._loadingPromiseMap[Type.frequentlyApps]) {
            this._loadingPromiseMap[Type.frequentlyApps] = Api.Preference.loadFrequentlyApps()
                .then((res) => {
                    if (!res.successful) {
                        return false;
                    }

                    this._frequentlyAppMap = res.returnData.reduce((map, i) => {
                        map[this._getFrequentlyAppType(i.device, i.isDefault)] = i;
                        return map;
                    }, {} as TFrequentlyAppMap);
                    return true;
                })
                .catch(() => {
                    return false;
                })
                .finally(() => {
                    Reflect.deleteProperty(this._loadingPromiseMap, Type.frequentlyApps);
                });
        }
        return this._loadingPromiseMap[Type.frequentlyApps]!;
    }

    async updateFrequentlyApps(data: H3.Preference.Request.IFrequentlyAppItem[]) {
        return Api.Preference.updateFrequentlyApps(data)
            .then((res) => {
                if (!res.successful) {
                    return false;
                }
                if (!this._frequentlyAppMap) {
                    this._frequentlyAppMap = {};
                }
                data.forEach((i) => {
                    this._frequentlyAppMap![this._getFrequentlyAppType(i.device, i.isDefault)] = i;
                });
                return true;
            })
            .catch(() => false);
    }

    afterSwitchEngine() {
        this._frequentlyAppMap = void 0;
        this.loadFrequentlyApps();
    }

    private _getFrequentlyAppType(device: H3.Util.Device, isDefault: boolean) {
        const keyCheck = [
            {
                check: device === H3.Util.Device.PC && isDefault,
                res: FrequentlyAppType.PCDefault,
            },
            {
                check: device === H3.Util.Device.PC && !isDefault,
                res: FrequentlyAppType.PC,
            },
            {
                check: device === H3.Util.Device.Mobile && isDefault,
                res: FrequentlyAppType.MobileDefault,
            },
            {
                check: device === H3.Util.Device.Mobile && !isDefault,
                res: FrequentlyAppType.Mobile,
            },
        ];
        for (const _item of keyCheck) {
            if (_item.check) {
                return _item.res;
            }
        }
        return FrequentlyAppType.PC;
    }
}
